"use client"
import { useAuth as useOidcAuth } from "react-oidc-context"
import { useEffect, useState } from "react"
import { WebStorageStateStore } from "oidc-client-ts"
import { createCognitoAuthConfig } from "@/lib/auth"
import { rememberReturnTo } from "@/lib/returnTo"
import { RECENT_PLAYERS_KEY } from "@/support/recentPlayers"

interface CognitoAuthConfig {
  authority?: string
  client_id?: string
  redirect_uri?: string
  post_logout_redirect_uri?: string
  response_type?: string
  scope?: string
  automaticSilentRenew?: boolean
  userStore?: WebStorageStateStore
}

export function useAuth() {
  const auth = useOidcAuth()
  const [authConfig, setAuthConfig] = useState<CognitoAuthConfig | null>(null)

  useEffect(() => {
    async function loadConfig() {
      try {
        const config = await createCognitoAuthConfig()
        setAuthConfig(config)
      } catch (error) {
        console.error("Failed to load auth configuration for signOut:", error)
      }
    }

    loadConfig()
  }, [])

  // If no AuthProvider context, return mock auth state (no authentication)
  if (!auth) {
    return {
      isAuthenticated: true,
      user: null,
      signIn: () => {},
      signOut: () => {},
      isLoading: false,
      error: null,
      token: null,
    }
  }

  return {
    isAuthenticated: auth.isAuthenticated,
    user: auth.user,
    // Remember the current path before the redirect leaves the app: the
    // Cognito callback lands on the origin root, and AutoSignin restores
    // this path once the sign-in completes (deep-link return, lib/returnTo).
    signIn: () => {
      if (typeof window !== "undefined") {
        rememberReturnTo(window.sessionStorage, window.location.pathname + window.location.search)
      }
      void auth.signinRedirect()
    },
    signOut: () => {
      // Clear this tab's per-user work state before the Cognito redirect, so
      // the next sign-in in this tab does not inherit the previous agent's
      // conversation pointer or recent-player list. This is a THIS-TAB
      // cleanup only: sessionStorage is per-tab, so other open tabs keep
      // their activeSessionId until they reload. That is contained, not
      // dangerous — session replay authorises on the JWT sub server-side and
      // the session UUID itself is not a secret. A cross-tab tombstone
      // broadcast was considered and rejected: clearing only the pointer in
      // other tabs would not sign them out (their OIDC token store, which
      // this cleanup deliberately never touches, keeps them authenticated
      // until token expiry), so it would imply a guarantee it cannot
      // deliver. Keep `sidebarOpen`, a per-machine UX preference that should
      // persist across sign-ins.
      if (typeof window !== "undefined") {
        window.sessionStorage.removeItem("activeSessionId")
        window.localStorage.removeItem(RECENT_PLAYERS_KEY)
      }

      const clientId = authConfig?.client_id || import.meta.env.VITE_COGNITO_CLIENT_ID || ""
      const logoutUri =
        authConfig?.redirect_uri ||
        import.meta.env.VITE_COGNITO_REDIRECT_URI ||
        "http://localhost:3000"

      auth.signoutRedirect({
        extraQueryParams: {
          client_id: clientId,
          logout_uri: logoutUri,
        },
      })
    },
    isLoading: auth.isLoading,
    error: auth.error,
    token: auth.user?.id_token,
  }
}
